import { useState } from 'react';
import { X } from 'lucide-react';
import { useSettings } from '@site/content';
import { useGoogleReviews } from '@api/public';
import { GoogleG, Stars } from './GoogleMarks';

const KEY = 'afs-google-badge-dismissed';
const readDismissed = () => {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

/** Floating Google rating pill (bottom-left; QuickContact owns bottom-right). */
export default function GoogleReviewBadge() {
  const { google = {} } = useSettings();
  const g = useGoogleReviews(google);
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed || String(google.showBadge || 'yes').toLowerCase() === 'no' || !g.rating || !g.mapsUrl) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(KEY, '1');
    } catch {
      /* private mode: dismiss for this page view only */
    }
  };

  return (
    <div className="pb-safe fixed bottom-4 left-4 z-30">
      <div className="group relative">
        <a
          href={g.mapsUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Rated ${g.rating} out of 5 from ${g.count} Google reviews. Opens Google Maps.`}
          className="flex items-center gap-2.5 rounded-full border border-black/5 bg-white py-2 pl-2.5 pr-4 shadow-lg shadow-black/15 transition-transform hover:-translate-y-0.5 sm:rounded-2xl sm:py-2.5 sm:pl-3 sm:pr-5"
        >
          <GoogleG className="h-7 w-7 shrink-0 sm:h-8 sm:w-8" />
          <span className="leading-tight">
            <span className="flex items-center gap-1.5">
              <span className="text-base font-bold text-ink-950">{Number(g.rating).toFixed(1)}</span>
              <Stars value={Number(g.rating)} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </span>
            <span className="hidden text-xs text-ink-500 sm:block">{g.count ? `${g.count} Google reviews` : 'Google reviews'}</span>
          </span>
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide Google rating"
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink-800 text-white opacity-90 shadow hover:bg-ink-950 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
